import TrackingService from '@/services/tracking';
import { theme } from '@/styles';
import { CustomScroll } from '@/styles/common';
import RadioBox from '@/ui/RadioBox';
import { formatTimePeriod } from '@/utils/date';
import { TimeCode } from '@remodeling/remodeling-manager';
import React, { useMemo, useState } from 'react';
import { DatePicker, Text, NewContentButton } from '@design-system/pc';
import styled, { css } from 'styled-components';
import { START_DATE_DIFF } from '@/constants/visit-consult';

interface DateTimePopupProps {
  times: TimeCode[];
  currentTime: TimeCode;
  currentDate: string;
  minDate?: Date;
  maxDate?: Date;
  closeModal: (value: boolean) => void;
  onChange: (value: { date: string; time: any }) => void;
}

const today = new Date();
const startDate = new Date(today);
startDate.setDate(today.getDate() + START_DATE_DIFF);

const DateTimePopup = ({
  currentDate,
  currentTime,
  minDate,
  maxDate,
  onChange,
  closeModal,
  times,
}: DateTimePopupProps) => {
  const [date, setDate] = useState(currentDate);
  const [time, setTime] = useState(currentTime);
  const selectedDate = useMemo(() => new Date(date), [date]);

  const handleClickDatePicker = (
    e: React.MouseEvent<HTMLElement, MouseEvent>,
  ) => {
    const { year, month, date } = e.currentTarget.dataset;
    const calcMonth = `${Number(month) < 10 ? '0' : ''}${month}`;
    const calcDate = `${Number(date) < 10 ? '0' : ''}${date}`;
    year && month && date && setDate(`${year}-${calcMonth}-${calcDate}`);
  };

  return (
    <Box {...TrackingService.tagGTM('visit_date_content')}>
      <ContentWrap>
        <CalendarBox>
          <DatePicker
            handleSelectDate={handleClickDatePicker}
            selectDate={selectedDate}
            isPreTodayDisabled
            minDate={minDate}
            maxDate={maxDate}
            width="336px"
          />
        </CalendarBox>
        <TimeBox>
          <Text
            variant="headline5"
            color={theme.colors.accent_colors.primary.default}
            marginBottom={24}
          >
            희망 시간 선택
          </Text>

          <TimeListBox>
            {times.map((item: any) => (
              <TimeCard
                key={item.code}
                onClick={() => setTime(item)}
                {...TrackingService.tagGTM('visit_consult_time')}
              >
                <Text
                  variant="body1"
                  color={theme.colors.accent_colors.primary.default}
                  width={'100%'}
                >
                  {formatTimePeriod(item.timeCode)}
                </Text>
                <RadioBox
                  disabled={false}
                  isChecked={item.code === time?.code}
                  radioType="blank"
                  {...TrackingService.tagGTM('visit_consult_time_button', {
                    'option-value': item.code,
                  })}
                />
              </TimeCard>
            ))}
          </TimeListBox>
        </TimeBox>
      </ContentWrap>
      <BtnContainer>
        <NewContentButton
          size={'lg'}
          emphasis="high"
          width="290px"
          onClick={() => {
            onChange({ date, time });
            closeModal(false);
          }}
          disabled={!date || !time}
          {...TrackingService.tagGTM('visit_date_popup_submit_button')}
        >
          확인
        </NewContentButton>
      </BtnContainer>
    </Box>
  );
};

export default DateTimePopup;

const Box = styled.div`
  width: 100%;
`;

const ContentWrap = styled.div`
  width: 100%;
  height: 437px;
  display: flex;
  justify-content: space-between;
  padding: 16px 28px 0px 28px;
`;

const CalendarBox = styled.div`
  width: 100%;
  padding: 20px 60px 0px 28px;
`;

const TimeBox = styled.div`
  ${({ theme }) => css`
    display: flex;
    width: 100%;
    flex-direction: column;
    border-left: 1px solid ${theme.colors.natural_color.gray[100]};
    padding-top: 20px;
    padding-left: 60px;
  `}
`;

const TimeListBox = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  flex-direction: column;
  overflow: auto;
  gap: 12px;
  ${CustomScroll};
`;

const TimeCard = styled.div`
  ${({ theme }) => css`
    display: flex;
    align-items: center;
    height: 48px;
    padding: 0px 16px;
    cursor: pointer;
    border-radius: 2px;
    border: 1px solid ${theme.colors.natural_color.gray[200]};
  `}
`;

const BtnContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100px;
  width: 100%;
`;
